import { Injectable, Injector, computed, signal } from '@angular/core'
import { Preferences } from '@capacitor/preferences'
import { ModalComponent, ModalDismissAction, ModalStyle } from '@doka-shared/common'
import { ModalController, ModalOptions } from '@ionic/angular'
import { Translation } from '../services/translation.service'
import { AuthenticationRepository } from './authentication-repository.service'
import { filter, first, firstValueFrom } from 'rxjs'
import { Router } from '@angular/router'

const STORAGE_KEY = 'device-mode'

@Injectable({ providedIn: 'root' })
export class DeviceModeRepository {
  private initialized = false
  private _isMobileSignal = signal<boolean | null>(null)

  constructor(private readonly injector: Injector) {}
  // Public read-only signal
  readonly isMobileSignal = computed(() => {
    void this.loadIfNeeded() // Trigger lazy init
    return this._isMobileSignal()
  })

  // Explicit setter
  async setIsMobileMode(value: boolean): Promise<void> {
    this._isMobileSignal.set(value)
    await Preferences.set({ key: STORAGE_KEY, value: String(value) })
  }

  // Internal detection logic
  private detectIsMobile(): boolean {
    const ua = navigator.userAgent || navigator.vendor
    return /android|iphone|ipad|mobile/i.test(ua)
  }

  // Lazy loader with modal side-effect
  private async loadIfNeeded(): Promise<void> {
    const authRepo = this.injector.get(AuthenticationRepository)

    // Wait until authenticated$ emits `true`
    await firstValueFrom(
      authRepo.authenticated$.pipe(
        filter((value): value is true => value === true),
        first()
      )
    )

    if (this.initialized || this._isMobileSignal() !== null) return
    this.initialized = true

    const { value } = await Preferences.get({ key: STORAGE_KEY })

    if (value !== null) {
      this._isMobileSignal.set(value === 'true')
    } else {
      const isMobileDetected = this.detectIsMobile()
      this._isMobileSignal.set(isMobileDetected)
      await Preferences.set({ key: STORAGE_KEY, value: String(isMobileDetected) })

      // don't show modal if user is working on desktop
      if (!isMobileDetected) return

      const translate = this.injector.get(Translation)
      const modalOpts: ModalOptions = {
        component: ModalComponent,
        cssClass: 'small-modal',
        componentProps: {
          title: translate.translate('SETTINGS.DEVICE_MODE.MODAL_TITLE'),
          text: translate.translate('SETTINGS.DEVICE_MODE.MODAL_DESCRIPTION'),
          cancelButtonLabel: translate.translate('SETTINGS.DEVICE_MODE.MODAL_BUTTON_SECONDARY'),
          confirmButtonLabel: translate.translate('SETTINGS.DEVICE_MODE.MODAL_BUTTON_PRIMARY'),
          modalStyle: ModalStyle.Information,
          icon: 'lift_to_talk',
        },
      }

      const modalCtrl = this.injector.get(ModalController)
      const modal = await modalCtrl.create(modalOpts)
      await modal.present()
      const { role } = await modal.onDidDismiss()

      if (role === ModalDismissAction.CANCEL) {
        const router = this.injector.get(Router)
        await router.navigate(['/settings'])
      }
    }
  }
}
